import _ from "lodash";
/* eslint-disable react/display-name */
import React, {
  useState,
  memo,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";

// Components
import AssessmentIcon from "UIComponents/AssessmentIcon";
import ProfileCascade from "UIComponents/ProfileCascade";
import I18nHOC from "UIComponents/I18nHOC";
import StatusIcon from "../StatusIcon";
import withQueryParamsBasedInterface from "UIComponents/QueryParamsBasedInterfaceHOC";
import AssignmentActionsDropdown from "ClassRoom/components/AssignmentActionsDropdown";
import AssignmentActionDialog from "AppComponents/AssignmentActionDialog";
import SendAlertMessageModal from "ClassRoom/routes/ClassRoomDetails/routes/AllSubmissions/components/SendAlertMessageModal";
import CourseIcon from "Teacher/components/CourseIcon";
import AssignmentEvaluationAICallout from "AppComponents/AssignmentEvaluationAICallout";

// Design System
import {
  InformationOutlined,
  InsightsSquareOutlined,
  ChevronLeftOutlined,
  ChevronRightOutlined,
  EyeHiddenOutlined,
  MinusCircleOutlined,
  PencilOutlined,
  StudentAddOutlined,
  LockOutlined,
  LockOpenOutlined,
  ClockAlarmOutlined,
  TickCircleOpenOutlined,
  StarsTwoFilled,
  CopyOutlined,
} from "@toddle-edu/ds-icons";
import {
  Button,
  IconButton,
  SplitButtonDropdown,
  DropdownMenu,
  Tooltip,
  Tag,
  Badge,
  useKeyboardShortcuts,
  useKeyboardShortcutsContext,
  Dropdown,
  ButtonDisclosure,
  Modal,
  TextAreaInput,
} from "@toddle-edu/ds-web";
import { colors } from "@toddle-edu/ds-theme";

// Utils
import { ASSESSMENT_TOOLS } from "@toddle-edu/common-utils";
import { multiLineEllipsis, getDateTimeStringsBasedOnFormat } from "Utils";
import {
  formatDueDate,
  getClassroomPropsMemoize,
  getAssignmentStatusLabel,
  getMediaManagerDropdownOptions,
  getStudentAssignmentSubmissionStatus,
  getCurrentButtonActionObject,
  getStudentTags,
  getSubjectLevelLabel,
  getAllSubmissionsStatusRevamp,
  getSubmissionStatus,
  getSubmissionUnlockState,
  getSomeToolsHaveEval,
  getIsSendAlertMessageEnabled,
  getActiveAndArchiveStudents,
  getIsEvaluationApplicable,
  getIsBenchmarkAIGradingEnabled,
  validateStudentSubmissionForAIEvaluation,
  getLockedStateToSendWithEvaluationAction,
} from "ClassRoom/modules/utils";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import CreateOrEditAssignment from "AppComponents/CreateOrEditAssignment";
import StudentAlerts from "AppComponents/StudentAlerts";
import {
  getFormattedUserName,
  getUserIdentifierName,
} from "Utils/GetFormattedUserName";
import {
  getSelectedCurriculumId,
  getUserArchivalStatus,
} from "modules/CommonSelectors";
import { goToBack } from "modules/NavigationModule";
import { getIsGradingPeriodUnlocked } from "GradingSetup/modules/Utils";
import { getIsLearningCoursesEnabled } from "Teacher/routes/LearningCourses/modules/Utils";
import {
  getIsAssignmentCopyLinkOptionEnabled,
  getIsEnabledExcusedStatusInAssignment,
  getIsAllSubmissionsNavigationImprovementsEnabled,
  getIsSubmissionStatusOverrideEnabled,
} from "Utils/ACLStoreUtils";

// Constants
import { fontStyle } from "Constants";
import { ASSESSMENT_TYPE_WORKSHEET } from "Worksheet/modules/WorksheetConstants";
import {
  ASSESSMENT_TYPE_AI_TUTOR,
  TASK_TYPE_AI_TUTOR,
  HOME_PAGE,
  TASK_TYPE_WORKSHEET,
  STUDENT,
} from "Constants/stringConstants";
import {
  AI_GRADING_PROGRESS_MAPPING,
  LOCK_STATE,
} from "ClassRoom/modules/Constants";

// External lib.
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import classNames from "classnames";

// Services
import { goToRelativeRoute, GENERIC_TAGS_INFO } from "modules/Services";
import ACLStore from "lib/aclStore";
import withDerivedProps from "lib/withDerivedProps";

// Modules
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import {
  unHandStudentAssignmentSubmission,
  onClickEvalUpdate,
  navigateToNextStudent,
  navigateToPreviousStudent,
  getAssessmentObj,
  handleQuickApproveAIEvaluation,
  updateStateForAssignment,
  setAIGradingProgress,
  createAiAssessmentEvaluation,
} from "ClassRoom/modules/ClassRoomModule";
import { getIsAIGeneratingResponseForTool } from "ClassRoom/modules/ClassRoomSelectors";
import { writeStudentAssigmentSubmissionInCache } from "ClassRoom/modules/ClassRoomGraphqlHelpers";
import { getIsWorksheetLayoutUpdateEnabled } from "Worksheet/modules/WorksheetFeatureFlags";

// Scss
import classes from "./HeaderV2.scss";
import { getToddleDocAttachment } from "AppComponents/ToddleDoc/Modules/ToddleDocModule";
import BreadCrumbs from "../BreadCrumbs";

const styles = {
  avatarStyles: {
    size: "medium",
    randomizeColor: false,
  },
  mediaMangerTextStyle: {
    ...multiLineEllipsis({ lineHeight: "2.4rem", maxCharacter: "30" }),
    maxHeight: "2.4rem",
  },
  mediaManagerDetailLeftStyle: {
    width: "100%",
  },
  allSubmissionLeftItemStyle: {
    marginRight: "16px",
  },
  mediaManagerLeftItemStyle: {
    marginRight: "8px",
  },
  mediaManagerLeftItemStyleWithBreadcrumbs: {
    marginRight: "4px",
  },
  warningTextStyles: {
    color: colors.textSubtle,
    fontSize: "1.6rem",
  },
  backButtonStyle: {
    cursor: "pointer",
  },
  instructionsTextStyle: {
    fontSize: "12px",
    lineHeight: "16px",
  },
  middleItemTextStyle: {
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "20px",
    color: colors.textDefault,
  },
  innerContainerStyle: {
    justifyContent: "flex-start",
  },
  mediaManagerInnerRightDetailContainerStyle: {
    marginRight: "12px",
  },
  mediaManagerInnerRightDetailContainerStyleWithBreadcrumbs: {
    marginRight: "8px",
  },
  overlayStyle: {
    maxWidth: 250,
  },
  scoreCategoryLabel: {
    fontSize: "1.2rem",
  },
  headerStyles: {
    fontSize: "2rem",
    padding: "24px 16px 16px 32px",
  },
  modalBodyStyle: {
    fontSize: "1.6rem",
    padding: "16px 32px",
    lineHeight: "2.4rem",
    color: colors.textDefault,
    ...fontStyle.medium,
  },
  footerContainerStyle: {
    padding: "32px",
  },
  overlayInnerStyle: {
    maxWidth: "120px",
    textAlign: "center",
  },
  starsTwoTertiaryFilled: {
    color: "var(--interactive-tertiary)",
  },
  starsTwoSubtleFilled: {
    color: "var(--icon-disabled)",
  },
  tagLabelStyle: { width: "auto" },
  tagWrapperStyle: { padding: 0 },
  tagContainerStyle: { padding: 0 },
  dropdownOverlayStyle: { zIndex: 10001 },
  tooltipContainer: { width: "240px" },
  classSwitcherOverlayStyle: { zIndex: 10002 },
  tooltipStyle: { maxWidth: "258px" },
};

const showPdftronV2 = ACLStore.can("FeatureFlag:PDFTronRevampedUI");

/** Send postMessage to the pdftron iframe to check if we
 * can perform any discard operation while the pdf is in edit mode */
const sendDiscardMessageToPdftron = ({ pdfTronInstance, trigger, key }) => {
  pdfTronInstance?.UI?.iframeWindow.postMessage(
    {
      message: "SHOULD_DISCARD_CHANGES",
      trigger,
      key,
    },
    "*"
  );
};

// MediaManager components
const MediaManagerLeftExtraItems = memo(props => {
  const { currentStudentData = {}, studentFullName, showBreadCrumbs } = props;
  const { student = {} } = currentStudentData;
  const { id, profileImage } = student;

  const isStudentArchived = getUserArchivalStatus(student);

  return id ? (
    <ProfileCascade
      items={[
        {
          id,
          value: studentFullName,
          imageUrl: profileImage,
        },
      ]}
      showAvatar={true}
      avatarStyles={styles.avatarStyles}
      isArchived={isStudentArchived}
      dsVersion={showBreadCrumbs ? "2.0" : "1.0"}
    />
  ) : null;
});

const MediaManagerSubText = memo(props => {
  const {
    currentStudentData = {},
    t,
    isStudentSubmissionEnabled,
    deadline,
    isTeacherEvaluationEnabled,
    isPrivate,
    isTimedTestEnabled,
    onOverrideClick,
    onResetOverride,
    studentAssignmentId,
    isWorksheet,
    isAITutor,
  } = props;

  const {
    status,
    submission = {},
    isEvaluated,
    isEvaluationApplicable,
    isSharedWithStudent,
    lockingState = {},
  } = currentStudentData;

  const isOverrideEnabled = getIsSubmissionStatusOverrideEnabled({
    isWorksheet,
    isAITutor,
  });

  const handleStatusClick = useCallback(() => {
    if (onOverrideClick) {
      onOverrideClick({
        studentAssignmentId,
        student: currentStudentData.student,
        submission: currentStudentData.submission,
        isLocked: _.get(lockingState, "state") === LOCK_STATE.LOCKED,
      });
    }
  }, [onOverrideClick, studentAssignmentId, currentStudentData, lockingState]);

  const handleResetOverrideClick = useCallback(() => {
    if (onResetOverride) {
      onResetOverride({ studentAssignmentId });
    }
  }, [onResetOverride, studentAssignmentId]);

  if (_.isEmpty(status)) return null;

  const { submissionStatus } = getSubmissionStatus({
    submission,
    assignmentDeadline: deadline,
    status,
  });

  const effectiveStatusForLabel =
    isOverrideEnabled && _.get(submission, "statusV2")
      ? _.get(submission, "statusV2")
      : status;

  const studentAssignmentStatusLabel = getAssignmentStatusLabel({
    status: effectiveStatusForLabel,
    t,
  });

  const studentSubmissionStatusLabel =
    status === "OVERDUE"
      ? studentAssignmentStatusLabel
      : getStudentAssignmentSubmissionStatus({
          submissionStatus,
          t,
        });

  const statusInfo = getAllSubmissionsStatusRevamp({
    columnId: "submission",
    t,
    isTeacherEvaluationEnabled,
    isEvaluated,
    isSharedWithStudent,
    isEvaluationApplicable: getIsEvaluationApplicable(isEvaluationApplicable),
    submission,
    omitKeys: ["tagIcon"],
    isPrivate,
    status,
  });

  const { showStatusIcon } = statusInfo;

  const showAssignmentStatus =
    studentAssignmentStatusLabel !== studentSubmissionStatusLabel &&
    (!getIsEnabledExcusedStatusInAssignment() ||
      status !== "EVALUATION_NOT_APPLICABLE");

  const hideSubmissionStatus =
    isStudentSubmissionEnabled &&
    (_.isEmpty(studentSubmissionStatusLabel) ||
      (getIsEnabledExcusedStatusInAssignment() && !showStatusIcon));

  const showLockingIcon =
    !isPrivate && !isTimedTestEnabled && isStudentSubmissionEnabled;

  const showStudentAssignmentStatus =
    isStudentSubmissionEnabled &&
    !!studentAssignmentStatusLabel &&
    showAssignmentStatus;

  const submissionNotApplicableLabel = t("common:label_not_applicable", {
    label: t("common:submission"),
  });

  const studentAssignmentStatusClass = classNames(
    classes.studentAssignmentStatus,
    classes.wordBreakNormal
  );

  const renderSubmissionStatus = () => {
    if (!isStudentSubmissionEnabled) {
      return <span>{submissionNotApplicableLabel}</span>;
    }
    if (hideSubmissionStatus) return null;

    const overrideConfig = isOverrideEnabled
      ? {
          onOverrideClick: handleStatusClick,
          onResetOverride: handleResetOverrideClick,
          systemStatus: _.get(submission, "systemStatus", null),
          statusV2: _.get(submission, "statusV2", null),
          isStudentUpdatedOverriddenStatus:
            _.get(submission, "overrideStatusUpdatedBy.type") === STUDENT,
        }
      : null;

    return (
      <div className={studentAssignmentStatusClass}>
        <StatusIcon
          showStatusIcon={statusInfo.showStatusIcon}
          statusTagState={statusInfo.statusTagState}
          statusIcon={statusInfo.statusIcon}
          statusTooltip={statusInfo.statusTooltip}
          resubmissionTooltip={statusInfo.resubmissionTooltip}
          tagLabelStyle={styles.tagLabelStyle}
          tagWrapperStyle={styles.tagWrapperStyle}
          tagContainerStyle={styles.tagContainerStyle}
          overrideConfig={overrideConfig}
          isResubmission={statusInfo.isResubmission}
        />
      </div>
    );
  };

  const renderLockingIcon = () => {
    const isSubmissionLocked = _.get(lockingState, "state") === "LOCKED";

    const SubmissionLockingIcon = isSubmissionLocked
      ? LockOutlined
      : LockOpenOutlined;
    const submissionLockingLabel = isSubmissionLocked
      ? t("classRoom:submission_closed")
      : t("classRoom:submission_open");

    return (
      <>
        {showAssignmentStatus || !hideSubmissionStatus ? (
          <span className={classes.dotContainer} />
        ) : null}
        <div className={classes.lockingContainer}>
          <SubmissionLockingIcon variant={"subtle"} size={"xxxx-small"} />
          <div className={classes.lockingLabel}>{submissionLockingLabel}</div>
        </div>
      </>
    );
  };

  return (
    <>
      {renderSubmissionStatus()}

      {showStudentAssignmentStatus ? (
        <>
          {!hideSubmissionStatus ? (
            <span className={classes.dotContainer} />
          ) : null}
          <div className={classes.studentAssignmentStatus}>
            {studentAssignmentStatusLabel}
          </div>
        </>
      ) : null}

      {showLockingIcon ? renderLockingIcon() : null}
    </>
  );
});

const MediaManagerLeftItem = memo(props => {
  const {
    onClickLeft,
    onClickRight,
    t,
    selectedStatusStudents,
    isPdftronEnabled,
    isTheatreModeV2Active,
    isWorkbookEnabled,
    isGraphTheaterModeEnabled,
    viewType,
  } = props;
  const pdftronV2Enabled = isPdftronEnabled && showPdftronV2;
  const isDisable = _.size(selectedStatusStudents) <= 1 || pdftronV2Enabled;

  const iconConClass = classNames({
    [classes.iconButtonCon]: isDisable,
  });

  const iconButtonClass = classNames({
    [classes.iconButtonDisabled]: isDisable,
  });
  const { areShortcutsEnabled } = useKeyboardShortcutsContext();

  const handleShortcuts = pressedKeys => {
    if (
      isTheatreModeV2Active ||
      isPdftronEnabled ||
      isWorkbookEnabled ||
      isGraphTheaterModeEnabled
    ) {
      return;
    }
    const { code } = _.first(pressedKeys);
    switch (code) {
      case "KeyA": {
        onClickLeft({ isShortcut: true });
        break;
      }
      case "KeyD": {
        onClickRight({ isShortcut: true });
        break;
      }
    }
  };

  useKeyboardShortcuts({
    handleShortcuts,
    maxShortcutLength: 1,
    areShortcutsEnabled,
  });

  const getToolTip = ({ label, key }) => {
    return (
      <div className={classes.tooltipContainer}>
        <div>{label}</div>
        <div className={classes.keyWrapper}>{key}</div>
      </div>
    );
  };

  return (
    <div className={classes.leftRightIcons}>
      <Tooltip
        tooltip={getToolTip({
          label: t("common:previous_with_label", {
            label: t("common:student"),
          }),
          key: "A",
        })}
        placement={"bottom"}
        trigger={areShortcutsEnabled ? ["hover"] : [null]}
      >
        <div className={iconConClass}>
          <IconButton
            icon={<ChevronLeftOutlined />}
            variant="plain"
            size="medium"
            onClick={onClickLeft}
            disabled={isDisable}
            className={iconButtonClass}
            testId={`${viewType}-navigateToPrevStudent`}
            aria-label={t("common:previous_with_label", {
              label: t("common:student"),
            })}
          />
        </div>
      </Tooltip>

      <Tooltip
        tooltip={getToolTip({
          label: t("common:next_with_label", {
            label: t("common:student"),
          }),
          key: "D",
        })}
        placement={"bottom"}
        trigger={areShortcutsEnabled ? ["hover"] : [null]}
      >
        <div className={iconConClass}>
          <IconButton
            icon={<ChevronRightOutlined />}
            variant="plain"
            size="medium"
            onClick={onClickRight}
            disabled={isDisable}
            className={iconButtonClass}
            testId={`${viewType}-navigateToNextStudent`}
            aria-label={t("common:next_with_label", {
              label: t("common:student"),
            })}
          />
        </div>
      </Tooltip>
    </div>
  );
});

const MediaManagerRightExtraItems = memo(props => {
  const {
    t,
    academicTermId,
    isPrivate,
    showEvaluationDropdown,
    currentStudentData,
    assignmentId,
    isCurrentAcademicYearSelected,
    isArchivedClass,
    setCurrentDialogueAction,
    isStudentSubmissionEnabled,
    onMarkEvaluatedShare,
    onMarkEvaluationApplicable,
    isWorkbookEnabled,
    isPdftronEnabled,
    isScoreVisible,
    isTeacherEvaluationEnabled,
    isStudentEvaluationEnabled,
    onClickMarkEvaluatedBtn,
    onClickEditEvaluationBtn,
    onClickEvaluationApplicableBtn,
    onClickShareWithStudentBtn,
    pdfTronInstance,
    isLockSubmissionEnabled,
    deadline,
    viewType,
    isGradingPeriodUnlocked = true,
    assessmentType,
    taskTypeGroup,
    assignmentData,
    isAiEvaluationEnabled,
    canPerformActionsInAssignment = true,
    showQuickApproveButton,
    pendingAIEvaluation,
    handleQuickApproveAIEvaluation,
    changeIsLoading,
    assessmentTools,
    isAIGeneratingResponseForTool,
    setAIGradingProgress,
    studentAssignmentId,
    createAiAssessmentEvaluation,
    handleDisableStopEvaluationButton,
    isAITutor,
    isWorksheet,
    isTimedTestEnabled,
  } = props;

  const [
    showRegenerateAIEvaluationModal,
    setShowRegenerateAIEvaluationModal,
  ] = useState(false);

  const [
    regenerateAIEvaluationInstruction,
    setRegenerateAIEvaluationInstruction,
  ] = useState("");

  const {
    isSubmitted,
    isEvaluated,
    isEvaluationApplicable,
    isSharedWithStudent,
  } = currentStudentData;

  const buttonActionObj =
    _.isFunction(getCurrentButtonActionObject) &&
    getCurrentButtonActionObject({
      t,
      isEvaluationApplicable,
      isSharedWithStudent,
      isEvaluated,
      isPrivate,
      isTeacherEvaluationEnabled,
      onClickMarkEvaluatedBtn,
      onClickEditEvaluationBtn,
      onClickEvaluationApplicableBtn,
      onClickShareWithStudentBtn,
    });

  useEffect(() => {
    const handleMessageFromPDFTron = event => {
      const data = event?.data;

      if (data?.message === "CAN_DISCARD_CHANGES") {
        switch (data?.trigger) {
          case "MARK_AS_COMPLETE_BUTTON": {
            buttonActionObj?.onClick();
            break;
          }
          case "DROPDOWN_OPTION": {
            onClickSplitDropdownOption({ key: data?.key });
            break;
          }
        }
      }
    };
    window.addEventListener("message", handleMessageFromPDFTron);
    return () => {
      window.removeEventListener("message", handleMessageFromPDFTron);
    };
  }, [
    buttonActionObj,
    isStudentSubmissionEnabled,
    isEvaluated,
    isSubmitted,
    isSharedWithStudent,
    isEvaluationApplicable,
    onMarkEvaluatedShare,
    onMarkEvaluationApplicable,
    setCurrentDialogueAction,
  ]);

  const onClickSplitDropdownOption = ({ key }) => {
    const lockState = getLockedStateToSendWithEvaluationAction({
      isWorksheetOrAiTutor: isWorksheet || isAITutor,
      isStudentSubmissionEnabled,
      isPrivate,
      isTimedTestEnabled,
    });

    switch (key) {
      case "evaluate":
        if (pendingAIEvaluation && isAiEvaluationEnabled) {
          setCurrentDialogueAction("EVALUATE_QUICK_APPROVE");
        } else if (isStudentSubmissionEnabled && !isSubmitted) {
          setCurrentDialogueAction("EVALUATE");
        } else {
          onMarkEvaluatedShare({
            isEvaluated: !isEvaluated,
            lockState,
          });
        }
        break;
      case "edit_evaluation":
        onMarkEvaluatedShare({
          isEvaluated: !isEvaluated,
          isSharedWithStudent: isSharedWithStudent ? false : undefined,
          lockState:
            isStudentSubmissionEnabled && isPrivate ? "UNLOCKED" : undefined,
        });
        break;
      case "share":
        if (isStudentSubmissionEnabled && !isSubmitted) {
          setCurrentDialogueAction("SHARE");
        } else {
          onMarkEvaluatedShare({
            isSharedWithStudent: true,
            lockState,
          });
        }
        break;
      case "evaluate_share":
        if (pendingAIEvaluation && isAiEvaluationEnabled) {
          setCurrentDialogueAction("EVALUATE_SHARE_QUICK_APPROVE");
        } else if (isStudentSubmissionEnabled && !isSubmitted) {
          setCurrentDialogueAction("EVALUATE_SHARE");
        } else {
          onMarkEvaluatedShare({
            isEvaluated: true,
            isSharedWithStudent: true,
            lockState,
          });
        }
        break;
      case "request_resubmission":
        setCurrentDialogueAction("RETURN");
        break;
      case "evaluation_not_applicable":
      case "enable_evaluation":
      case "mark_as_excused":
      case "remove_excused_status":
        onMarkEvaluationApplicable({
          isEvaluationApplicable: !isEvaluationApplicable,
          evalStatus: "EVAL_APPLICABLE",
        });
        break;
      case "open_for_submissions":
        onMarkEvaluatedShare({
          lockState: getSubmissionUnlockState({
            isLockSubmissionEnabled,
            deadline,
          }),
        });
        break;
      case "close_for_submissions":
        onMarkEvaluatedShare({
          lockState: "LOCKED",
          assignedStudents: currentStudentData,
          assessmentDetails: assignmentData?.content,
        });
        break;
    }
  };

  const {
    student = {},
    toolResponses,
    ibPYPElementRatings,
    elementRemarks,
    submission,
    aiEvaluationJob,
  } = currentStudentData;

  const isBenchmarkAIGradingEnabled = getIsBenchmarkAIGradingEnabled();

  const { id: studentId } = student;

  const isStudentArchived = getUserArchivalStatus(student);

  const oldPdftronEnabled = isPdftronEnabled && !showPdftronV2;

  const disableMarkAsCompleteButton = isWorkbookEnabled || oldPdftronEnabled;

  const pdftronV2Enabled = isPdftronEnabled && showPdftronV2;

  const attachmentGroups = _.get(submission, "attachmentGroups");

  const hasStudentAddedSubmission = _.size(attachmentGroups) > 0;

  const hasStudentAddedResponse = !_.isEmpty(_.get(submission, "response"));

  const { errorMessage } = validateStudentSubmissionForAIEvaluation({
    attachmentGroups,
    t,
  });

  const disableAIEvaluationGenerateButton =
    isAIGeneratingResponseForTool || errorMessage;

  const disableButton =
    !isCurrentAcademicYearSelected ||
    isStudentArchived ||
    isArchivedClass ||
    disableMarkAsCompleteButton ||
    !isGradingPeriodUnlocked;

  const disabledTooltipForLockedTerm = (
    <div style={styles.tooltipContainer}>
      {t(
        "academicSetup:assignment_is_in_locked_grading_period_no_edits_are_allowed"
      )}
    </div>
  );

  const latestAIEvaluationJobStatus = _.get(aiEvaluationJob, "0.status", "");

  const hasAIToolResponse = _.reduce(
    toolResponses,
    (result, edge) => {
      const aiResponse = _.filter(edge.toolResponses, { creatorType: "AI" });
      return result.concat(aiResponse);
    },
    []
  );

  const hasAnyToolResponse =
    _.some(toolResponses, response => response?.toolResponses?.length > 0) ||
    _.size(ibPYPElementRatings) > 0 ||
    _.size(elementRemarks) > 0;

  const hasAnyAIResponse =
    _.size(hasAIToolResponse) > 0 ||
    _.some(ibPYPElementRatings, { creatorType: "AI" }) ||
    _.some(elementRemarks, {
      creatorType: "AI",
      ibPYPElementType: "STUDENT_ASSIGNMENT",
    });

  const showRedoAIEvaluationButton =
    isAiEvaluationEnabled &&
    !isBenchmarkAIGradingEnabled &&
    !isEvaluated &&
    getIsEvaluationApplicable(isEvaluationApplicable) &&
    hasAnyAIResponse &&
    isTeacherEvaluationEnabled &&
    (hasStudentAddedSubmission || hasStudentAddedResponse);

  const showGenerateAIEvaluationButton =
    isAiEvaluationEnabled &&
    !isBenchmarkAIGradingEnabled &&
    !isEvaluated &&
    getIsEvaluationApplicable(isEvaluationApplicable) &&
    (hasAnyToolResponse ||
      _.size(toolResponses) > 0 ||
      latestAIEvaluationJobStatus === "COMPLETED" ||
      latestAIEvaluationJobStatus === "FAILED") &&
    !hasAnyAIResponse &&
    (hasStudentAddedSubmission || hasStudentAddedResponse) &&
    isGradingPeriodUnlocked;

  const options = getMediaManagerDropdownOptions({
    isUsingScore: isScoreVisible,
    currentStudentData,
    assessmentType,
    taskTypeGroup,
    ...props,
  });

  const onMarkAsCompleteClick = () => {
    if (pdftronV2Enabled) {
      sendDiscardMessageToPdftron({
        pdfTronInstance,
        trigger: "MARK_AS_COMPLETE_BUTTON",
      });
      return;
    }
    buttonActionObj?.onClick();
  };

  const onDropdownOptionClicked = ({ key }) => {
    if (pdftronV2Enabled) {
      sendDiscardMessageToPdftron({
        pdfTronInstance,
        trigger: "DROPDOWN_OPTION",
        key,
      });
      return;
    }
    onClickSplitDropdownOption({ key });
  };

  const onClickQuickApproveAIEvaluation = async () => {
    changeIsLoading(true);

    await handleQuickApproveAIEvaluation({
      currentStudentData,
      assignmentId,
      academicTermId,
      assessmentTools,
      learningCourseId: _.get(assignmentData, "content.learningCourse.id"),
    });

    changeIsLoading(false);
  };

  const generateAIResponse = async () => {
    handleDisableStopEvaluationButton(true);
    setAIGradingProgress({
      [studentAssignmentId]:
        AI_GRADING_PROGRESS_MAPPING["analyzingAssignmentCriteria"],
    });

    await createAiAssessmentEvaluation({
      input: {
        assessmentId: _.get(assignmentData, "content.id"),
        studentAssignmentId,
        studentId: studentId,
      },
    });

    handleDisableStopEvaluationButton(false);
  };

  const handleRegenerateAIResponse = async () => {
    setShowRegenerateAIEvaluationModal(false);
    setRegenerateAIEvaluationInstruction("");
    handleDisableStopEvaluationButton(true);

    setAIGradingProgress({
      [studentAssignmentId]:
        AI_GRADING_PROGRESS_MAPPING["analyzingAssignmentCriteria"],
    });

    await createAiAssessmentEvaluation({
      input: {
        assessmentId: _.get(assignmentData, "content.id"),
        studentAssignmentId,
        studentId: studentId,
        metadata: {
          regenerateAIGrading: true,
          userInstruction: regenerateAIEvaluationInstruction,
        },
      },
    });

    handleDisableStopEvaluationButton(false);
  };

  const toggleShowRegenerateAIEvaluationModal = () => {
    setShowRegenerateAIEvaluationModal(!showRegenerateAIEvaluationModal);
  };

  const splitDropDownWrapper = (
    <SplitButtonDropdown
      overlay={
        <DropdownMenu
          options={options}
          showSelection
          onClick={onDropdownOptionClicked}
          disabled={disableButton || isAIGeneratingResponseForTool}
          testId={`${viewType}-studentEvaluationDropdown`}
        />
      }
      disabled={disableButton || isAIGeneratingResponseForTool}
    />
  );

  const renderRegenerateAIEvaluationModal = () => {
    const handleKeyDown = e => {
      if (
        e.key === "Enter" &&
        !_.isEmpty(_.trim(regenerateAIEvaluationInstruction))
      ) {
        e.preventDefault();
        handleRegenerateAIResponse();
      }
    };

    return (
      <Modal
        width="440px"
        height="fit-content"
        isOpen={showRegenerateAIEvaluationModal}
        onClose={toggleShowRegenerateAIEvaluationModal}
        dsVersion="2.0"
      >
        <Modal.Header
          title={t("ai:redo_ai_evaluation_for_all")}
          isClosable={true}
        />

        <Modal.Body>
          <div className={classes.regenerateModalBody}>
            <div className="text-label">
              {t("ai:how_would_you_like_to_improve_ai_evaluation")}
            </div>
            <TextAreaInput
              dsVersion="2.0"
              value={regenerateAIEvaluationInstruction}
              placeholder={t("ai:regenerate_ai_evaluation_placeholder")}
              onChange={e => {
                setRegenerateAIEvaluationInstruction(e.target.value);
              }}
              onKeyDown={handleKeyDown}
            />
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div className={classes.footerContainer}>
            <Button
              dsVersion="2.0"
              size="medium"
              variant="neutral"
              type="outlined"
              onClick={toggleShowRegenerateAIEvaluationModal}
            >
              {t("common:cancel")}
            </Button>
            <Button
              dsVersion="2.0"
              size="medium"
              variant="progressive"
              onClick={handleRegenerateAIResponse}
              disabled={_.isEmpty(_.trim(regenerateAIEvaluationInstruction))}
            >
              {t("ai:regenerate")}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    );
  };

  return showEvaluationDropdown && canPerformActionsInAssignment ? (
    <>
      {showGenerateAIEvaluationButton ? (
        <Tooltip
          visible={errorMessage}
          dsVersion="2.0"
          tooltip={errorMessage && t("ai:disable_generate_button_text")}
        >
          <Button
            dsVersion="2.0"
            variant="neutral"
            type="outlined"
            icon={
              <StarsTwoFilled
                style={
                  disableAIEvaluationGenerateButton
                    ? styles.starsTwoSubtleFilled
                    : styles.starsTwoTertiaryFilled
                }
              />
            }
            size="medium"
            disabled={disableAIEvaluationGenerateButton}
            testId={`${viewType}-generate-ai--evaluation`}
            onClick={generateAIResponse}
          >
            {t("ai:generate_ai_evaluation")}
          </Button>
        </Tooltip>
      ) : null}

      {isAiEvaluationEnabled &&
      showQuickApproveButton &&
      pendingAIEvaluation &&
      isTeacherEvaluationEnabled &&
      !isBenchmarkAIGradingEnabled ? (
        <>
          <Tooltip
            tooltip={t("ai:approve_evaluation_for_all_assessment_tools")}
            dsVersion="2.0"
          >
            <Button
              dsVersion="2.0"
              variant="neutral"
              type="outlined"
              icon={<TickCircleOpenOutlined />}
              size="medium"
              disabled={isAIGeneratingResponseForTool}
              testId={`${viewType}-quick-approve-evaluation`}
              onClick={onClickQuickApproveAIEvaluation}
            >
              {t("ai:quick_approve_ai_evaluation")}
            </Button>
          </Tooltip>
          <div className={classes.separator} />
        </>
      ) : null}

      {showRedoAIEvaluationButton ? (
        <Tooltip
          dsVersion="2.0"
          tooltip={
            errorMessage
              ? t("ai:disable_generate_button_text")
              : t("ai:redo_ai_evaluation_for_all")
          }
          overlayInnerStyle={styles.tooltipStyle}
        >
          <IconButton
            dsVersion="2.0"
            variant="neutral"
            type="outlined"
            icon={
              <StarsTwoFilled
                style={
                  disableAIEvaluationGenerateButton
                    ? styles.starsTwoSubtleFilled
                    : styles.starsTwoTertiaryFilled
                }
              />
            }
            size="medium"
            disabled={disableAIEvaluationGenerateButton}
            testId={`${viewType}-redo-ai--evaluation`}
            onClick={toggleShowRegenerateAIEvaluationModal}
          />
        </Tooltip>
      ) : null}

      {isAiEvaluationEnabled ? <AssignmentEvaluationAICallout /> : null}

      <ConditionalWrapper
        condition={!isGradingPeriodUnlocked}
        wrapper={
          <Tooltip tooltip={disabledTooltipForLockedTerm} placement="left" />
        }
        type="wrapperAsElement"
      >
        <div>
          <ConditionalWrapper
            condition={!_.isEmpty(options)}
            wrapper={splitDropDownWrapper}
            type="wrapperAsElement"
          >
            <Button
              dsVersion="2.0"
              size={"medium"}
              onClick={onMarkAsCompleteClick}
              disabled={disableButton}
              variant={"progressive"}
              testId={`${viewType}-markAsComplete`}
            >
              {buttonActionObj?.buttonText}
            </Button>
          </ConditionalWrapper>
        </div>
      </ConditionalWrapper>

      {showRegenerateAIEvaluationModal
        ? renderRegenerateAIEvaluationModal()
        : null}
    </>
  ) : null;
});

const EvaluationInsightButton = memo(props => {
  const {
    t,
    isScheduled,
    hasAssessmentTool,
    toggleShowEvaluationInsights,
    toggleAIInsights,
    assessmentTools,
    viewType,
    assessmentType,
    taskTypeGroup,
    showDotBadge,
    disableActionButtons = false,
  } = props;

  const showEvalInsights = _.some(assessmentTools?.edges, ({ node }) => {
    const currentTool = _.find(ASSESSMENT_TOOLS, {
      type: node?.assessmentToolType,
    });

    return _.get(currentTool, "hasInsight", false);
  });

  const isAITutor =
    taskTypeGroup === TASK_TYPE_AI_TUTOR ||
    assessmentType == ASSESSMENT_TYPE_AI_TUTOR;

  const showEvalInsightsButton =
    (!isScheduled && hasAssessmentTool && showEvalInsights) || isAITutor;

  return showEvalInsightsButton ? (
    isAITutor ? (
      <ConditionalWrapper
        condition={showDotBadge}
        wrapper={
          <Badge
            dsVersion="2.0"
            type="dot"
            size="xxx-small"
            variant="active"
            offset={[2, 2]}
          />
        }
        type="wrapperAsElement"
      >
        <Button
          icon={<InsightsSquareOutlined />}
          variant="outlined-subtle"
          onClick={toggleAIInsights}
          testId={`${viewType}-evaluationInsights`}
          disabled={disableActionButtons}
        >
          {t("ai:ai_insights")}
        </Button>
      </ConditionalWrapper>
    ) : (
      <Button
        icon={<InsightsSquareOutlined />}
        variant="outlined-subtle"
        onClick={toggleShowEvaluationInsights}
        testId={`${viewType}-evaluationInsights`}
        disabled={disableActionButtons}
      >
        {t("assessmentEvaluation:evaluation_insights")}
      </Button>
    )
  ) : null;
});

const AllSubmissionsSubText = memo(props => {
  const {
    t,
    deadline,
    isScoreEnabled,
    isPrivate,
    scoreCategoryLabel,
    allFields,
    genericTags,
    isLEPreview,
    isExcludedFromFinalGradeCalculation = false,
    assignmentData,
    state,
    isStudentSubmissionEnabled,
    isTimedTestEnabled,
    isLockScreenEnabled,
    isSafeBrowserEnabled,
    isWorksheetLayoutUpdateEnabled,
  } = props;

  const subjectLevelLabel = getSubjectLevelLabel({
    fields: allFields,
    genericTags,
    showColoredTags: true,
  });

  const categoryContainerClass = classNames({
    [classes.categoryContainer]: true,
    [classes.categoryContainerNoWrap]: isLEPreview,
  });

  const publishedAt = _.get(assignmentData, "state.publishedAt", "");
  const dueDate = formatDueDate({ deadline, t });

  const dateToDisplay =
    state === "SCHEDULED"
      ? `${t("common:scheduled_for_with_label", {
          label: "",
        })}: ${
          publishedAt
            ? getDateTimeStringsBasedOnFormat({
                inputDateTimeString: publishedAt,
              }).dateTimeString
            : "-"
        }`
      : dueDate;

  const renderLockingIcon = () => {
    const assignedStudents = _.get(assignmentData, "assignedStudents", []);
    const openSubmissionsCount = _.size(
      _.filter(
        assignedStudents,
        item => _.get(item, "lockingState.state", {}) !== "LOCKED"
      )
    );

    const totalCount = _.size(assignedStudents);

    const tooltip =
      openSubmissionsCount > 0
        ? openSubmissionsCount === totalCount
          ? t("classRoom:task_is_open_for_submissions_for_all_students")
          : t("classRoom:task_is_open_for_submissions_for_count_students", {
              openSubmissionsCount,
              totalCount,
            })
        : t("classRoom:task_is_closed_for_submissions_for_all_students");

    const SubmissionLockingIcon =
      openSubmissionsCount > 0 ? LockOpenOutlined : LockOutlined;
    const submissionLockingLabel =
      openSubmissionsCount > 0 ? t("common:open") : t("common:closed");

    return (
      <>
        <Tooltip tooltip={tooltip} placement={"bottomRight"}>
          <div className={classes.lockingContainer}>
            <SubmissionLockingIcon variant={"subtle"} size={"xxx-small"} />
            <div>{submissionLockingLabel}</div>
          </div>
        </Tooltip>
        <span className={classes.dotContainer} />
      </>
    );
  };

  return (
    <React.Fragment>
      {!_.includes(["SCHEDULED", "DRAFT"], state) &&
      !isPrivate &&
      !isTimedTestEnabled &&
      isStudentSubmissionEnabled
        ? renderLockingIcon()
        : null}
      {isExcludedFromFinalGradeCalculation ? (
        <React.Fragment>
          <Tooltip
            placement={"bottom"}
            tooltip={t("classRoom:excluded_from_final_grade_calculation")}
            overlayInnerStyle={styles.overlayInnerStyle}
          >
            <MinusCircleOutlined
              size={"xxx-small"}
              variant={"subtle"}
              className={classes.noShrink}
            />
          </Tooltip>
          <span className={classes.dotContainer} />
        </React.Fragment>
      ) : null}

      <div className={classes.scoreDetailContainer}>
        {scoreCategoryLabel ? (
          <React.Fragment>
            <Tooltip
              tooltip={t("common:category_with_label", {
                label: scoreCategoryLabel,
              })}
              placement={"bottom"}
            >
              <span className={categoryContainerClass}>
                {scoreCategoryLabel}
              </span>
            </Tooltip>

            <span className={classes.dotContainer} />
          </React.Fragment>
        ) : null}
      </div>

      {!_.isEmpty(subjectLevelLabel) ? (
        <React.Fragment>
          {subjectLevelLabel}
          <span className={classes.dotContainer} />
        </React.Fragment>
      ) : null}

      {isPrivate && (
        <React.Fragment>
          <div className={classes.flexWithGap}>
            <Tooltip tooltip={t("common:private")} placement={"bottom"}>
              <EyeHiddenOutlined
                size={"xxx-small"}
                variant={"subtle"}
                className={classes.noShrink}
              />
            </Tooltip>
            {!isScoreEnabled || !scoreCategoryLabel
              ? `${t("common:private")} `
              : null}
          </div>
          <span className={classes.dotContainer} />
        </React.Fragment>
      )}

      {isWorksheetLayoutUpdateEnabled &&
        (isLockScreenEnabled || isSafeBrowserEnabled) && (
          <React.Fragment>
            <span className={classes.dueDate}>
              {t("worksheet:security_mode")}:{" "}
              {isLockScreenEnabled
                ? t("worksheet:browser_lock")
                : t("worksheet:lockdown_application")}
            </span>
            <span className={classes.dotContainer} />
          </React.Fragment>
        )}

      <Tooltip tooltip={dateToDisplay} placement={"bottom"}>
        <span className={classes.dueDate}>{dateToDisplay}</span>
      </Tooltip>
    </React.Fragment>
  );
});

const InstructionButton = ({
  instructionButtonVariant = "outlined-subtle",
  instructionButtonSize = "medium",
  showIcon = true,
  instructionsText = "common:instruction_plural",
  instructionsTextStyle = {},
  onInstructionsClick,
  isWorkbookEnabled,
  isPdftronEnabled,
  rightPane,
  t,
  viewType,
}) => {
  const oldPdftronEnabled = isPdftronEnabled && !showPdftronV2;
  const disableInstructionButton = isWorkbookEnabled || oldPdftronEnabled;
  return (
    <Button
      icon={showIcon ? <InformationOutlined /> : null}
      variant={instructionButtonVariant}
      size={instructionButtonSize}
      onClick={onInstructionsClick}
      disabled={disableInstructionButton}
      isActivated={!_.isEmpty(rightPane)}
      testId={`${viewType}-intructions`}
    >
      <div style={instructionsTextStyle}>{t(instructionsText)}</div>
    </Button>
  );
};

const DraftOrScheduledModeButtons = ({
  t,
  onClickEdit,
  onClickAssign,
  isArchivedClass,
  isCurrentAcademicYearSelected,
  isGradingPeriodUnlocked,
}) => {
  const disabled =
    isArchivedClass ||
    !isCurrentAcademicYearSelected ||
    !isGradingPeriodUnlocked;

  const disabledTooltipWithLabel = label =>
    !isGradingPeriodUnlocked ? (
      <div style={styles.tooltipContainer}>
        {t("academicSetup:you_cannot_with_label_since_locked", {
          label,
        })}
      </div>
    ) : null;

  return (
    <div className={classes.buttonContainer}>
      <ConditionalWrapper
        condition={!isGradingPeriodUnlocked}
        wrapper={
          <Tooltip tooltip={disabledTooltipWithLabel(t("common:edit"))} />
        }
        type={"wrapperAsElement"}
      >
        <Button
          icon={<PencilOutlined />}
          variant="outlined-subtle"
          onClick={onClickEdit}
          disabled={disabled}
          aria-label={t("common:edit")}
          testId="allSubmissions-scheduledOrDraft-edit"
        >
          {t("common:edit")}
        </Button>
      </ConditionalWrapper>

      <ConditionalWrapper
        condition={!isGradingPeriodUnlocked}
        wrapper={
          <Tooltip tooltip={disabledTooltipWithLabel(t("common:assign"))} />
        }
        type={"wrapperAsElement"}
      >
        <Button
          icon={<StudentAddOutlined />}
          variant="progressive"
          onClick={onClickAssign}
          disabled={disabled}
          aria-label={t("common:assign")}
          testId="allSubmissions-scheduledOrDraft-assign"
        >
          {t("common:assign")}
        </Button>
      </ConditionalWrapper>
    </div>
  );
};

const AssignmentLinkedClassSwitcher = memo(props => {
  const {
    assignmentLinkedClasses,
    assignmentId,
    handleClassAssignmentSwitch,
  } = props;

  const currentClass = _.find(assignmentLinkedClasses, {
    assignmentId,
  });

  const handleClassSwitch = ({ key }) => {
    const index = _.findIndex(assignmentLinkedClasses, { key });
    if (index >= 0) {
      handleClassAssignmentSwitch(index);
    }
  };

  return (
    <div className={classes.linkedClassSwitcher}>
      <Dropdown
        overlay={
          <DropdownMenu
            options={assignmentLinkedClasses}
            onClick={handleClassSwitch}
            dsVersion="2.0"
          />
        }
        overlayStyle={styles.classSwitcherOverlayStyle}
        disabled={_.size(assignmentLinkedClasses) <= 1}
      >
        <ButtonDisclosure
          dsVersion="2.0"
          variant="neutral"
          type="plain"
          icon={<CourseIcon course={currentClass} />}
        >
          {currentClass?.title}
        </ButtonDisclosure>
      </Dropdown>
    </div>
  );
});

const HeaderItem = memo(props => {
  const {
    t,
    text,
    subText,
    leftItem,
    middleItem = null,
    rightItem,
    textStyle,
    leftItemStyle,
    leftExtraItems,
    isScheduled,
    rightExtraItems,
    isDraft = false,
    detailLeftContainerStyle,
    isMediaManager = false,
    showRightItem = true,
    isLEPreview = false,
    innerContainerStyle = {},
    leftExtraItemsStyle = {},
    canPerformActionsInAssignment = true,
    isWorksheet = false,
    setToastMsg,
    safeExamBrowserExitCode,
    isWorksheetLayoutUpdateEnabled,
    showBreadCrumbs,
    breadcrumbHierarchy,
    onBreadCrumbClick,
    studentFullName,
    isLinksRoute,
    isAllSubmissionsNavigationImprovementsEnabled,
  } = props;

  const displayText =
    !showBreadCrumbs && isMediaManager && studentFullName
      ? `${text} - ${studentFullName}`
      : `${text} - ${t("classRoom:all_submissions")}`;

  const showDisplayText =
    isAllSubmissionsNavigationImprovementsEnabled && isLinksRoute;

  const renderedLabel = showDisplayText ? displayText : text;

  const showTooltip = _.size(renderedLabel) > 30;

  const innerLeftContainerClass = classNames({
    [classes.innerLeftContainer]: true,
    [classes.mediaManagerInnerLeftContainer]:
      !isAllSubmissionsNavigationImprovementsEnabled && isMediaManager,
    [classes.mediaManagerInnerLeftContainerWithBreadcrumbs]:
      isMediaManager && isAllSubmissionsNavigationImprovementsEnabled,
  });

  const innerMiddleContainerClass = classNames({
    [classes.innerMiddleContainer]: middleItem,
    [classes.innerMiddleContainerWithBreadcrumbs]:
      middleItem && isAllSubmissionsNavigationImprovementsEnabled,
    [classes.noShrink]: isLEPreview,
  });

  const handleCopy = text => {
    navigator.clipboard.writeText(text);
    setToastMsg?.({
      msg: "toastMsgs:Successfully_copied_clipboard",
      type: "success",
    });
  };

  const getSEBExitCodeComponent = () => {
    const showText =
      !isScheduled &&
      !isMediaManager &&
      !isDraft &&
      isWorksheet &&
      !_.isEmpty(safeExamBrowserExitCode);

    return showText ? (
      <div className={classes.SEBTextContainer}>
        <div className={classes.SEBText}>
          {t("worksheet:seb_exit_code_with_label", {
            label: safeExamBrowserExitCode,
          })}
        </div>
        <IconButton
          size="x-small"
          icon={<CopyOutlined variant="subtle" />}
          _legacy_variant={"plain"}
          variant="neutral"
          onClick={() => handleCopy(safeExamBrowserExitCode)}
          testId={`allSubmissions-handleCopy`}
          dsVersion="2.0"
        />
      </div>
    ) : null;
  };

  return (
    <div className={classes.innerContainer} style={innerContainerStyle}>
      <div className={innerLeftContainerClass}>
        <div className={classes.innerLeftDetailContainer}>
          <div
            className={classes.detailLeftContainer}
            style={detailLeftContainerStyle}
          >
            {leftItem && (
              <div className={classes.leftItem} style={leftItemStyle}>
                {leftItem}
              </div>
            )}
            <div
              className={classes.innerRightDetailContainer}
              style={leftExtraItemsStyle}
            >
              {leftExtraItems}
            </div>
            <div className={classes.detailInfo}>
              <ConditionalWrapper
                condition={!showBreadCrumbs && showTooltip}
                wrapper={
                  <Tooltip
                    placement="bottom"
                    tooltip={showDisplayText ? displayText : text}
                  />
                }
                type="wrapperAsElement"
              >
                <div
                  className={
                    showBreadCrumbs
                      ? classes.breadcrumbTextContainer
                      : classes.textContainer
                  }
                >
                  {showBreadCrumbs ? (
                    <BreadCrumbs
                      hierarchy={breadcrumbHierarchy}
                      onHierarchyClick={onBreadCrumbClick}
                      isMediaManager={isMediaManager}
                    />
                  ) : (
                    <div
                      className={classNames("text-label", classes.text)}
                      style={textStyle}
                    >
                      {showDisplayText ? displayText : text}
                    </div>
                  )}
                  {isDraft && (
                    <Tag color="yellow" size="x-small">
                      {t("common:draft")}
                    </Tag>
                  )}
                </div>
              </ConditionalWrapper>
              <div className={classes.subtextContainer}>{subText}</div>
            </div>
          </div>
        </div>
      </div>
      <div className={innerMiddleContainerClass}>
        {middleItem}
        {showRightItem && (
          <div className={classes.innerRightContainer}>
            {getSEBExitCodeComponent()}
            {!isDraft ? rightItem : null}
            {!isScheduled &&
            !isMediaManager &&
            !isDraft &&
            !isWorksheetLayoutUpdateEnabled ? (
              <InstructionButton {...props} />
            ) : null}
            {canPerformActionsInAssignment && (isDraft || isScheduled) ? (
              <DraftOrScheduledModeButtons {...props} />
            ) : null}
            {rightExtraItems}
          </div>
        )}
      </div>
    </div>
  );
});

const HeaderV2 = memo(props => {
  const {
    t,
    state,
    rightPane,
    currentStep,
    headerLabel,
    onInstructionsClick,
    currentStudentData = {},
    isWorkbookEnabled,
    isPdftronEnabled,
    changePdftronEnable,
    closeRightPane,
    scoreCategoryLabel,
    unHandStudentAssignmentSubmission,
    setToastMsg,
    changeIsLoading,
    studentAssignmentId,
    isStudentSubmissionEnabled,
    onClickEvalUpdate,
    selectedStatusStudents,
    isPracticalActivity,
    isExperimentalProgramme,
    isLinksRoute,
    goToRelativeRoute,
    isTeacherEvaluationEnabled,
    studentCourseTags,
    isMediaManagerRoute,
    navigateToPreviousStudent,
    navigateToNextStudent,
    location,
    pdfTronInstance,
    assignmentData,
    courseData,
    totalNewSnpCycles,
    isClassStreamManagerRoute,
    isClassRoomAssignmentsRoute,
    isGradebookRoute,
    isCoursePlanRoute,
    isCalendarRoute,
    isClassFlowRoute,
    isTheatreModeV2Active,
    courseId,
    assessmentType,
    taskTypeGroup,
    showAssignmentActionsDropdown,
    currentCurriculumProgramId,
    isPrivate,
    viewType,
    isArchivedClass,
    isCurrentAcademicYearSelected,
    showSendAlertMesssageModalIcon,
    contentType,
    sortedAssignedStudents,
    setContainerRef = _.noop,
    redirectionSource,
    goToBack,
    allFields,
    canPerformActionsInAssignment = true,
    assignmentLinkedClasses = [],
    assignmentId,
    handleClassAssignmentSwitch,
    disableActionButtons = false,
    handleQuickApproveAIEvaluation,
    isOnlyStudentLeftToEvaluate,
    updateStateForAssignment,
    assessmentTools,
    isAIGeneratingResponseForTool,
    setAIGradingProgress,
    createAiAssessmentEvaluation,
    isSafeBrowserEnabled,
    handleDisableStopEvaluationButton,
    isTimedTestEnabled,
    attachmentId,
    getToddleDocAttachment,
    deadline,
    onOverrideClick,
    onResetOverride,
  } = props;

  const [currentDialogAction, setCurrentDialogueAction] = useState("");
  const [draftAction, setDraftAction] = useState(null);
  const [
    sendAlertMessageModalParams,
    setSendAlertMessageModalParams,
  ] = useState({});

  const containerRef = useRef(null);

  const isAllSubmissionsNavigationImprovementsEnabled = getIsAllSubmissionsNavigationImprovementsEnabled();

  useEffect(() => {
    // focus should start from the top header
    setContainerRef(containerRef);
    setTimeout(() => {
      containerRef.current?.focus();
    }, 300);
  }, []);

  const {
    student = {},
    isSubmitted,
    isEvaluated,
    isEvaluationApplicable,
  } = currentStudentData;

  const { id: studentId, alertTypes } = student;

  const isStudentArchived = getUserArchivalStatus(student);

  const studentFullName = getFormattedUserName({ user: student });

  const studentIdentifierName = getUserIdentifierName({
    user: student,
  });

  const isAllSubmission = _.isEqual(currentStep, "allsubmissions");
  const isLEPreview = _.isEqual(currentStep, "LEPreview");
  const isClassDiscussion = _.isEqual(currentStep, "classdiscussion");
  const isMediaManager = _.isInteger(parseInt(currentStep));
  const isWorksheetRoutes = _.includes(
    ["scorebook", "monitoring", "evaluation-insights", "details"],
    currentStep
  );

  const isScheduled = state === "SCHEDULED";
  const isDraft = state === "DRAFT";

  const submission = _.get(currentStudentData, "submission");
  const submissionId = _.get(submission, "id");
  const conversationId = _.get(currentStudentData, "conversation.id");

  const isSharedWithStudent = _.get(
    currentStudentData,
    "isSharedWithStudent",
    false
  );

  const isWorksheet =
    taskTypeGroup === TASK_TYPE_WORKSHEET ||
    assessmentType === ASSESSMENT_TYPE_WORKSHEET;
  const isAITutor =
    taskTypeGroup === TASK_TYPE_AI_TUTOR ||
    assessmentType === ASSESSMENT_TYPE_AI_TUTOR;

  const isDraftSubmission = _.get(submission, "isDraftSubmission", false);

  const learningCourseId = _.get(assignmentData, "content.learningCourse.id");

  const { toolResponses, elementRemarks, ibPYPElementRatings } =
    currentStudentData || {};

  const hasAIToolResponse = _.reduce(
    toolResponses,
    (result, edge) => {
      const aiResponse = _.filter(edge.toolResponses, { creatorType: "AI" });
      return result.concat(aiResponse);
    },
    []
  );

  const academicTermId = _.get(assignmentData, "content.academicTerm.id");

  const pendingAIEvaluation =
    _.some(hasAIToolResponse, { isDeleted: false }) ||
    _.some(ibPYPElementRatings, { creatorType: "AI", isDeleted: false }) ||
    _.some(elementRemarks, {
      creatorType: "AI",
      isDeleted: false,
      ibPYPElementType: "STUDENT_ASSIGNMENT",
    });

  const showQuickApproveButton =
    (!_.isEmpty(hasAIToolResponse) ||
      _.some(ibPYPElementRatings, { creatorType: "AI", isDeleted: false }) ||
      _.some(elementRemarks, {
        creatorType: "AI",
        isDeleted: false,
        ibPYPElementType: "STUDENT_ASSIGNMENT",
      })) &&
    getIsEvaluationApplicable(isEvaluationApplicable) &&
    !isEvaluated;

  const isWorksheetLayoutUpdateEnabled =
    getIsWorksheetLayoutUpdateEnabled() && isWorksheet;

  useEffect(() => {
    const handleMessageFromPDFTron = event => {
      const data = event?.data;

      if (data?.message === "CAN_DISCARD_CHANGES") {
        switch (data?.trigger) {
          case "BACK_BUTTON": {
            onClickBack();
            break;
          }
        }
      }
    };
    window.addEventListener("message", handleMessageFromPDFTron);
    return () => {
      window.removeEventListener("message", handleMessageFromPDFTron);
    };
  }, [
    isPdftronEnabled,
    isMediaManager,
    isMediaManagerRoute,
    isPracticalActivity,
    isExperimentalProgramme,
  ]);

  const { combinedActiveAndArchivedStudents } = getActiveAndArchiveStudents({
    assignedStudents: selectedStatusStudents,
  });

  const onClickLeft = ({ isShortcut = false }) => {
    navigateToPreviousStudent({
      isShortcut,
      selectedStatusStudents: combinedActiveAndArchivedStudents,
      studentAssignmentId,
      location,
    });
  };

  const onClickRight = ({ isShortcut = false }) => {
    navigateToNextStudent({
      isShortcut,
      selectedStatusStudents: combinedActiveAndArchivedStudents,
      studentAssignmentId,
      location,
    });
  };

  const requestResubmission = async promptText => {
    const params = {
      id: submissionId,
      status: "RETURNED",
      statusMessage: {
        label: promptText,
        itemType: "ATTACHMENTS",
        parentId: conversationId,
      },
    };

    changeIsLoading(true);
    const res = await unHandStudentAssignmentSubmission(params);
    changeIsLoading(false);
    // if unsuccessful show toast an throw error to avoid data loss and revert optimistic changes in cache
    if (!res.successful) {
      writeStudentAssigmentSubmissionInCache({
        id: submissionId,
        data: { ...submission, status: "SUBMITTED" },
      });
      setToastMsg(res.errors[0]);
    } else {
      writeStudentAssigmentSubmissionInCache({
        id: submissionId,
        data: { ...submission, status: "RETURNED" },
      });
      setToastMsg({
        msg: "common:request_resubmission",
        type: "success",
        position: "toast-bottom-left",
      });
    }
  };

  const onClickEvaluationApplicableBtn = () => {
    onClickEvalUpdate({
      isEvaluated,
      isSharedWithStudent,
      isEvaluationApplicable: !isEvaluationApplicable,
      studentAssignmentId,
      changeIsLoading,
      assignmentId,
    });
  };

  const onClickMarkEvaluatedBtn = () => {
    const lockState = getLockedStateToSendWithEvaluationAction({
      isWorksheetOrAiTutor: isWorksheet || isAITutor,
      isStudentSubmissionEnabled,
      isPrivate,
      isTimedTestEnabled,
    });

    if (isEvaluated) {
      onClickEvalUpdate({
        isEvaluated: !isEvaluated,
        isSharedWithStudent: isSharedWithStudent ? false : undefined,
        studentAssignmentId,
        changeIsLoading,
        lockState:
          isStudentSubmissionEnabled && isPrivate ? "UNLOCKED" : undefined,
        assignmentId,
      });
      return;
    }

    if (pendingAIEvaluation && isAiEvaluationEnabled) {
      setCurrentDialogueAction("EVALUATE_QUICK_APPROVE");
    } else if (isStudentSubmissionEnabled && !isSubmitted) {
      setCurrentDialogueAction("EVALUATE");
    } else {
      onClickEvalUpdate({
        isEvaluated: !isEvaluated,
        studentAssignmentId,
        changeIsLoading,
        lockState,
        assessmentDetails: assignmentData?.content,
        assignedStudents: currentStudentData,
        assignmentId,
      });
    }
  };

  const onClickEditEvaluationBtn = () => {
    const lockState = getLockedStateToSendWithEvaluationAction({
      isWorksheetOrAiTutor: isWorksheet || isAITutor,
      isStudentSubmissionEnabled,
      isPrivate,
      isTimedTestEnabled,
    });

    if (onClickEvalUpdate) {
      onClickEvalUpdate({
        isSharedWithStudent: isSharedWithStudent ? false : undefined,
        isEvaluated: false,
        studentAssignmentId,
        changeIsLoading,
        lockState,
        assignmentId,
      });
    }
  };

  const onClickShareWithStudentBtn = () => {
    const lockState = getLockedStateToSendWithEvaluationAction({
      isWorksheetOrAiTutor: isWorksheet || isAITutor,
      isStudentSubmissionEnabled,
      isPrivate,
      isTimedTestEnabled,
    });

    if (onClickEvalUpdate) {
      onClickEvalUpdate({
        isSharedWithStudent: true,
        studentAssignmentId,
        changeIsLoading,
        assignmentId,
        lockState,
      });
    }
  };

  const onMarkEvaluationApplicable = params => {
    onClickEvalUpdate({
      ...params,
      studentAssignmentId,
      changeIsLoading,
      assignmentId,
    });
  };

  const onMarkEvaluatedShare = async params => {
    const { isMoveToArchived } = params;

    await onClickEvalUpdate({
      ...params,
      studentAssignmentId,
      changeIsLoading,
      assignmentId,
    });
    if (isMoveToArchived) {
      try {
        changeIsLoading(true);
        await updateStateForAssignment({
          id: assignmentId,
          state: "ARCHIVED",
        });
        changeIsLoading(false);
      } catch (error) {
        console.error(error);
      }
    }
  };

  const onCloseDialogBox = () => {
    setCurrentDialogueAction("");
  };

  const onDialogConfirmClick = async params => {
    const { closeSubmissions } = params || {};
    const prompText = _.get(params, "remark", "");

    const lockState = getLockedStateToSendWithEvaluationAction({
      closeSubmissions,
      isWorksheetOrAiTutor: isWorksheet || isAITutor,
      isStudentSubmissionEnabled,
      isPrivate,
      isTimedTestEnabled,
    });

    const { moveToReviewed } = params;

    onCloseDialogBox();

    switch (currentDialogAction) {
      case "RETURN":
        requestResubmission(prompText);
        break;
      case "EVALUATE_QUICK_APPROVE":
      case "EVALUATE": {
        if (currentDialogAction === "EVALUATE_QUICK_APPROVE") {
          changeIsLoading(true);
          await handleQuickApproveAIEvaluation({
            currentStudentData,
            assignmentId,
            academicTermId,
            assessmentTools,
            learningCourseId,
          });
        }
        await onMarkEvaluatedShare({
          isEvaluated: !isEvaluated,
          lockState,
          assessmentDetails: assignmentData?.content,
          assignedStudents: currentStudentData,
          isMoveToArchived: moveToReviewed ? moveToReviewed : undefined,
        });
        changeIsLoading(false);
        break;
      }
      case "SHARE":
        onMarkEvaluatedShare({
          isSharedWithStudent: true,
          lockState,
        });
        break;
      case "EVALUATE_SHARE":
      case "EVALUATE_SHARE_QUICK_APPROVE": {
        if (currentDialogAction === "EVALUATE_SHARE_QUICK_APPROVE") {
          changeIsLoading(true);
          await handleQuickApproveAIEvaluation({
            currentStudentData,
            assignmentId,
            academicTermId,
            assessmentTools,
            learningCourseId,
          });
        }
        await onMarkEvaluatedShare({
          isEvaluated: true,
          isSharedWithStudent: true,
          lockState,
          isMoveToArchived: moveToReviewed ? moveToReviewed : undefined,
        });
        changeIsLoading(false);
        break;
      }
    }
  };

  const onClickEdit = () => {
    setDraftAction("EDIT");
  };

  const onClickAssign = () => {
    setDraftAction("ASSIGN");
  };

  const onCloseEditAssignmentModal = () => {
    setDraftAction(null);
  };

  const containerClass = classNames({
    [classes.container]: true,
    [classes.mediaManagerContainer]: isMediaManager,
    [classes.lePreviewContainer]: isLEPreview,
  });

  const closeRightPaneHandler = () => {
    if (rightPane) {
      closeRightPane?.();
    }
  };

  const getStudentNameWithTagComponent = () => {
    const tags = getStudentTags({ studentCourseTags, studentId });

    return (
      <div className={classes.studentNameWithTagContainer}>
        <div className={classes.text}>{studentFullName}</div>
        <StudentAlerts studentId={studentId} alertTypes={alertTypes} />
        {isStudentArchived ? (
          <div className={classes.archivedText}>
            {`(${t("common:archived")})`}
          </div>
        ) : null}
        {!_.isEmpty(tags) && (
          <div className={classes.tagsContainer}>
            {_.map(tags, tag => {
              return (
                <Tag
                  key={tag?.label}
                  size={"xx-small"}
                  color={_.get(
                    GENERIC_TAGS_INFO,
                    `${tag?.label}.color`,
                    "teal"
                  )}
                >
                  {tag?.label}
                </Tag>
              );
            })}
          </div>
        )}
        {getIsEnabledExcusedStatusInAssignment() && !isEvaluationApplicable ? (
          <Tag color={"yellow"} dsVersion={"2.0"} size={"small"}>
            {t("classRoom:excused")}
          </Tag>
        ) : null}
      </div>
    );
  };

  const StudentNameWithTagComponent = getStudentNameWithTagComponent();

  const pdftronV2Enabled = isPdftronEnabled && showPdftronV2;

  const onClickBackHelper = () => {
    if (pdftronV2Enabled) {
      sendDiscardMessageToPdftron({
        pdfTronInstance,
        trigger: "BACK_BUTTON",
      });
      return;
    }
    onClickBack();
  };

  const getSpecificRoute = route => {
    switch (true) {
      case isPracticalActivity:
        return `${route}/../practicalactivities`;
      case isExperimentalProgramme:
        return `${route}/../recordofexperimentalprogrammes`;
      default:
        return route;
    }
  };

  const onClickBack = () => {
    if (isPdftronEnabled) {
      changePdftronEnable(false);
    }

    if (isAllSubmissionsNavigationImprovementsEnabled && isMediaManager) {
      if (isMediaManagerRoute) {
        goToRelativeRoute({
          route: "../../allsubmissions",
          type: "push",
        });
      } else {
        goToRelativeRoute({
          route: getSpecificRoute("../../../.."),
          type: "push",
        });
      }
      return;
    }

    if (
      _.isEqual(redirectionSource, "NOTIFICATION") ||
      _.isEqual(redirectionSource, "STUDENT_PROFILE") ||
      _.isEqual(redirectionSource, HOME_PAGE)
    ) {
      // If redirected from notification, go back to the source location
      goToBack();
      return;
    }

    if (isLinksRoute) {
      //if it is a refreshed links route, it will be coming from notifications/emails so go back to homepage
      goToRelativeRoute({
        route: `/platform/${currentCurriculumProgramId}/courses`,
        type: "push",
      });
      return;
    }

    if (!isAllSubmissionsNavigationImprovementsEnabled && isMediaManager) {
      if (isMediaManagerRoute) {
        goToRelativeRoute({
          route: getSpecificRoute("../../../.."),
          type: "push",
        });
      } else {
        goToRelativeRoute({ route: "../allsubmissions", type: "push" });
      }
    } else {
      goToRelativeRoute({ route: getSpecificRoute("../../.."), type: "push" });
    }
  };

  const onActionDone = useMemo(() => {
    const onDelete = () => {
      if (isLinksRoute) {
        //if it is a links route, it will be coming from notifications/emails so go back to homepage
        goToRelativeRoute({ route: "../../../../..", type: "replace" });
      } else {
        goToRelativeRoute({ route: "../../..", type: "replace" });
      }
    };
    return {
      DELETE: onDelete,
      delete: onDelete,
    };
  }, [isLinksRoute]);

  const getLeftItem = params => {
    const { showBackButton = true } = params || {};

    return (
      <div className={classes.leftItemParent}>
        {showBackButton ? (
          <div onClick={onClickBackHelper}>
            <IconButton
              variant="plain"
              icon={<ChevronLeftOutlined variant="subtle" size="xx-small" />}
              testId={`${viewType}-back`}
              aria-label={t("common:back")}
            />
          </div>
        ) : null}

        {getIsLearningCoursesEnabled() &&
        isAllSubmission &&
        _.size(assignmentLinkedClasses) > 1 ? (
          <AssignmentLinkedClassSwitcher
            assignmentLinkedClasses={assignmentLinkedClasses}
            assignmentId={assignmentId}
            handleClassAssignmentSwitch={handleClassAssignmentSwitch}
          />
        ) : null}

        <div className={classes.iconContainer}>
          <AssessmentIcon
            assessmentType={assessmentType}
            contentType={contentType}
          />
        </div>
      </div>
    );
  };

  const allSubmissionRightItem = isWorksheetLayoutUpdateEnabled ? null : (
    <EvaluationInsightButton
      {...props}
      isScheduled={isScheduled}
      disableActionButtons={disableActionButtons}
    />
  );

  const breadcrumbHierarchy = useMemo(() => {
    const getParentLabel = () => {
      if (isGradebookRoute) {
        return t("common:gradebook");
      }
      if (isCoursePlanRoute) {
        return t("course:course_flow");
      }
      if (isCalendarRoute) {
        return t("common:calendar");
      }
      if (isClassFlowRoute) {
        return t("configuration:class_flow");
      }
      if (isClassRoomAssignmentsRoute && !isClassStreamManagerRoute) {
        return t("common:assignment_other");
      }
      return t("common:assignments_manager");
    };

    const parentLabel = getParentLabel();

    const getWorksheetRouteLabel = () => {
      switch (currentStep) {
        case "scorebook":
          return t("worksheet:scorebook");
        case "monitoring":
          return t("worksheet:monitoring");
        case "evaluation-insights":
          return t("assessmentEvaluation:evaluation_insights");
        case "details":
          return t("common:instruction_plural");
        default:
          return t("classRoom:all_submissions");
      }
    };

    if (isMediaManager) {
      return [
        {
          id: "assignment_manager",
          label: parentLabel,
        },
        {
          id: "assignment_title",
          label: headerLabel,
        },
      ];
    }

    return [
      {
        id: "assignment_manager",
        label: parentLabel,
      },
      {
        id: "assignment_title",
        label: isAllSubmission
          ? headerLabel
          : `${headerLabel} - ${getWorksheetRouteLabel()}`,
      },
    ];
  }, [
    isGradebookRoute,
    isCoursePlanRoute,
    isCalendarRoute,
    isClassFlowRoute,
    isClassRoomAssignmentsRoute,
    isClassStreamManagerRoute,
    isMediaManager,
    currentStep,
    headerLabel,
    isAllSubmission,
    t,
  ]);

  const handleBreadCrumbClick = useCallback(
    id => {
      switch (id) {
        case "assignment_manager":
          if (isMediaManager) {
            goToRelativeRoute({
              route: getSpecificRoute("../../../.."),
              type: "push",
            });
          } else if (isWorksheetRoutes || isAllSubmission) {
            goToRelativeRoute({
              route: getSpecificRoute("../../../"),
              type: "push",
            });
          } else {
            onClickBack();
          }
          break;
        case "assignment_title":
          if (isMediaManager) {
            goToRelativeRoute({ route: "../../allsubmissions", type: "push" });
          }
          break;
        default:
          break;
      }
    },
    [
      isMediaManager,
      goToRelativeRoute,
      getSpecificRoute,
      onClickBack,
      isWorksheetRoutes,
      isAllSubmission,
    ]
  );

  const handleSendAlertMessageIconClick = () => {
    setSendAlertMessageModalParams({ type: "reminder" });
  };

  const oldPdftronEnabled = isPdftronEnabled && !showPdftronV2;

  const transparentModalClass =
    draftAction === "ASSIGN" ? classes.transparentModal : "";

  const itemTitle =
    _.get(assignmentData, "content.title.value", "") ||
    _.get(assignmentData, "content.assignmentTitle", "") ||
    "";

  const assignedStudents = _.get(assignmentData, "assignedStudents", []);

  const currentStudent = _.find(assignedStudents, { id: studentAssignmentId });

  const isLTIAttachmentPresent = _.some(
    _.get(currentStudent, "submission.attachmentGroups", []),
    attachmentGroup =>
      _.get(attachmentGroup, "attachments[0].metadata.type", "") === "LTI_TOOL"
  );

  const isGradingPeriodUnlocked = getIsGradingPeriodUnlocked(
    _.get(assignmentData, "content.academicTerm"),
    assessmentType,
    taskTypeGroup
  );

  const safeExamBrowserExitCode = _.get(
    assignmentData,
    "safeExamBrowserQuitPassword"
  );

  const isAiEvaluationEnabled =
    _.get(_.find(allFields, { uid: "aiEvaluation" }), "value", false) &&
    getIsEvaluationApplicable(isEvaluationApplicable);

  const isEnabledBulkReminder =
    !isPrivate &&
    !isStudentArchived &&
    !isArchivedClass &&
    isCurrentAcademicYearSelected &&
    !isDraft &&
    !isScheduled &&
    isGradingPeriodUnlocked &&
    showSendAlertMesssageModalIcon &&
    getIsSendAlertMessageEnabled() &&
    canPerformActionsInAssignment &&
    !isWorksheetLayoutUpdateEnabled;

  const showAssignmentCopyLinkOption =
    getIsAssignmentCopyLinkOptionEnabled() &&
    isAllSubmission &&
    !isDraft &&
    !isScheduled;

  const showBreadCrumbs =
    isAllSubmissionsNavigationImprovementsEnabled &&
    !isLinksRoute &&
    (isClassStreamManagerRoute ||
      isClassRoomAssignmentsRoute ||
      isGradebookRoute ||
      isCoursePlanRoute ||
      isClassFlowRoute ||
      isCalendarRoute) &&
    !_.isEmpty(breadcrumbHierarchy);

  const rightExtraItems = (
    <>
      {isEnabledBulkReminder ? (
        <IconButton
          onClick={handleSendAlertMessageIconClick}
          icon={<ClockAlarmOutlined />}
          variant="outlined-subtle"
          aria-label={t("common:send_reminder")}
          disabled={disableActionButtons}
        />
      ) : null}
      {showAssignmentActionsDropdown &&
      !_.isEmpty(currentCurriculumProgramId) &&
      canPerformActionsInAssignment ? (
        <AssignmentActionsDropdown
          assignment={assignmentData}
          course={courseData}
          courseId={courseId}
          totalNewSnpCycles={totalNewSnpCycles}
          creationSource={
            isClassStreamManagerRoute
              ? "CLASS_STREAM_REVIEW_DETAILS"
              : "CLASSROOM_DETAILS"
          }
          overlayStyle={styles.dropdownOverlayStyle}
          onActionDone={onActionDone}
          buttonSize={"medium"}
          buttonVariant={"neutral"}
          viewType={viewType}
          dsVersion="2.0"
          buttonType={"outlined"}
          disabled={disableActionButtons}
          isTimedTestEnabled={isTimedTestEnabled}
          showAssignmentCopyLinkOption={showAssignmentCopyLinkOption}
        />
      ) : null}
    </>
  );

  return (
    <div
      className={containerClass}
      onClick={closeRightPaneHandler}
      ref={containerRef}
      tabIndex={-1}
    >
      {isMediaManager && (
        <HeaderItem
          t={t}
          leftItem={getLeftItem()}
          text={headerLabel}
          textStyle={styles.middleItemTextStyle}
          subText={
            <React.Fragment>
              {scoreCategoryLabel && (
                <React.Fragment>
                  <Tooltip
                    tooltip={t("common:category_with_label", {
                      label: scoreCategoryLabel,
                    })}
                    placement={"bottom"}
                  >
                    <span className={classes.categoryContainer}>
                      {scoreCategoryLabel}
                    </span>
                  </Tooltip>
                  <span className={classes.dotContainer} />
                </React.Fragment>
              )}

              <InstructionButton
                {...props}
                instructionButtonSize="small"
                instructionButtonVariant="inline"
                showIcon={false}
                instructionsText={"common:view_instructions"}
                instructionsTextStyle={styles.instructionsTextStyle}
              />
            </React.Fragment>
          }
          middleItem={
            <HeaderItem
              t={t}
              leftItem={
                <MediaManagerLeftItem
                  {...props}
                  academicTermId={academicTermId}
                  onClickLeft={onClickLeft}
                  onClickRight={onClickRight}
                  isTheatreModeV2Active={isTheatreModeV2Active}
                />
              }
              text={StudentNameWithTagComponent}
              subText={
                <MediaManagerSubText
                  isWorksheet={isWorksheet}
                  isAITutor={isAITutor}
                  currentStudentData={currentStudentData}
                  t={t}
                  isStudentSubmissionEnabled={isStudentSubmissionEnabled}
                  deadline={deadline}
                  isTeacherEvaluationEnabled={isTeacherEvaluationEnabled}
                  isPrivate={isPrivate}
                  isTimedTestEnabled={isTimedTestEnabled}
                  onOverrideClick={onOverrideClick}
                  onResetOverride={onResetOverride}
                  studentAssignmentId={studentAssignmentId}
                />
              }
              textStyle={styles.mediaMangerTextStyle}
              leftExtraItems={
                <MediaManagerLeftExtraItems
                  {...props}
                  studentFullName={studentFullName}
                  showBreadCrumbs={showBreadCrumbs}
                />
              }
              onInstructionsClick={onInstructionsClick}
              leftItemStyle={
                !showBreadCrumbs
                  ? styles.mediaManagerLeftItemStyle
                  : styles.mediaManagerLeftItemStyleWithBreadcrumbs
              }
              rightPane={rightPane}
              showRightItem={false}
              innerContainerStyle={styles.innerContainerStyle}
              detailLeftContainerStyle={styles.mediaManagerDetailLeftStyle}
              leftExtraItemsStyle={
                !showBreadCrumbs
                  ? styles.mediaManagerInnerRightDetailContainerStyle
                  : styles.mediaManagerInnerRightDetailContainerStyleWithBreadcrumbs
              }
              isDraft={isDraft}
            />
          }
          rightExtraItems={
            <MediaManagerRightExtraItems
              {...props}
              currentStudentData={currentStudentData}
              setCurrentDialogueAction={setCurrentDialogueAction}
              onClickMarkEvaluatedBtn={onClickMarkEvaluatedBtn}
              onClickEditEvaluationBtn={onClickEditEvaluationBtn}
              onClickEvaluationApplicableBtn={onClickEvaluationApplicableBtn}
              onClickShareWithStudentBtn={onClickShareWithStudentBtn}
              onMarkEvaluationApplicable={onMarkEvaluationApplicable}
              onMarkEvaluatedShare={onMarkEvaluatedShare}
              isWorksheet={isWorksheet}
              academicTermId={academicTermId}
              isAiEvaluationEnabled={isAiEvaluationEnabled}
              isGradingPeriodUnlocked={isGradingPeriodUnlocked}
              showQuickApproveButton={showQuickApproveButton}
              pendingAIEvaluation={pendingAIEvaluation}
              studentAssignmentId={studentAssignmentId}
              setAIGradingProgress={setAIGradingProgress}
              isAIGeneratingResponseForTool={isAIGeneratingResponseForTool}
              assessmentTools={assessmentTools}
              createAiAssessmentEvaluation={createAiAssessmentEvaluation}
              handleDisableStopEvaluationButton={
                handleDisableStopEvaluationButton
              }
              isAITutor={isAITutor}
              attachmentId={attachmentId}
              getToddleDocAttachment={getToddleDocAttachment}
            />
          }
          onInstructionsClick={onInstructionsClick}
          showDivider={true}
          instructionButtonSize={"medium"}
          isWorkbookEnabled={isWorkbookEnabled}
          isPdftronEnabled={isPdftronEnabled}
          rightPane={rightPane}
          isMediaManager={true}
          isDraft={isDraft}
          viewType={viewType}
          isClassStreamManagerRoute={isClassStreamManagerRoute}
          isClassRoomAssignmentsRoute={isClassRoomAssignmentsRoute}
          breadcrumbHierarchy={breadcrumbHierarchy}
          showBreadCrumbs={showBreadCrumbs}
          onBreadCrumbClick={handleBreadCrumbClick}
          studentFullName={studentFullName}
          isLinksRoute={isLinksRoute}
          isAllSubmissionsNavigationImprovementsEnabled={
            isAllSubmissionsNavigationImprovementsEnabled
          }
        />
      )}

      {isAllSubmission && (
        <HeaderItem
          t={t}
          leftItem={getLeftItem()}
          text={headerLabel}
          subText={
            <AllSubmissionsSubText
              isWorksheetLayoutUpdateEnabled={isWorksheetLayoutUpdateEnabled}
              {...props}
            />
          }
          rightItem={allSubmissionRightItem}
          isGradingPeriodUnlocked={isGradingPeriodUnlocked}
          onInstructionsClick={onInstructionsClick}
          leftItemStyle={styles.allSubmissionLeftItemStyle}
          rightPane={rightPane}
          isScheduled={isScheduled}
          isDraft={isDraft}
          rightExtraItems={rightExtraItems}
          onClickEdit={onClickEdit}
          onClickAssign={onClickAssign}
          viewType={viewType}
          canPerformActionsInAssignment={canPerformActionsInAssignment}
          isArchivedClass={isArchivedClass}
          isCurrentAcademicYearSelected={isCurrentAcademicYearSelected}
          isSafeBrowserEnabled={isSafeBrowserEnabled}
          isWorksheet={isWorksheet}
          setToastMsg={setToastMsg}
          safeExamBrowserExitCode={safeExamBrowserExitCode}
          isWorksheetLayoutUpdateEnabled={isWorksheetLayoutUpdateEnabled}
          isClassStreamManagerRoute={isClassStreamManagerRoute}
          isClassRoomAssignmentsRoute={isClassRoomAssignmentsRoute}
          isGradebookRoute={isGradebookRoute}
          isCoursePlanRoute={isCoursePlanRoute}
          isCalendarRoute={isCalendarRoute}
          isClassFlowRoute={isClassFlowRoute}
          showBreadCrumbs={showBreadCrumbs}
          breadcrumbHierarchy={showBreadCrumbs ? breadcrumbHierarchy : null}
          onBreadCrumbClick={handleBreadCrumbClick}
          isLinksRoute={isLinksRoute}
          isAllSubmissionsNavigationImprovementsEnabled={
            isAllSubmissionsNavigationImprovementsEnabled
          }
        />
      )}

      {isLEPreview ? (
        <HeaderItem
          t={t}
          leftItem={getLeftItem({ showBackButton: false })}
          text={headerLabel}
          subText={
            <AllSubmissionsSubText
              isLEPreview={isLEPreview}
              isWorksheetLayoutUpdateEnabled={isWorksheetLayoutUpdateEnabled}
              {...props}
            />
          }
          rightItem={null}
          onInstructionsClick={null}
          leftItemStyle={styles.allSubmissionLeftItemStyle}
          rightPane={rightPane}
          isScheduled={isScheduled}
          showRightItem={false}
          isLEPreview={true}
          isDraft={isDraft}
          isWorksheetLayoutUpdateEnabled={isWorksheetLayoutUpdateEnabled}
        />
      ) : null}

      {isClassDiscussion && (
        <HeaderItem
          t={t}
          leftItem={getLeftItem()}
          text={t("classRoom:class_discussion")}
          subText={headerLabel}
          rightItem={
            <EvaluationInsightButton
              {...props}
              disableActionButtons={disableActionButtons}
            />
          }
          onInstructionsClick={onInstructionsClick}
          rightPane={rightPane}
          isDraft={isDraft}
          isWorksheetLayoutUpdateEnabled={isWorksheetLayoutUpdateEnabled}
          isArchivedClass={isArchivedClass}
          isGradingPeriodUnlocked={isGradingPeriodUnlocked}
          isCurrentAcademicYearSelected={isCurrentAcademicYearSelected}
        />
      )}

      {!_.isEmpty(currentDialogAction) ? (
        <AssignmentActionDialog
          dialogAction={currentDialogAction}
          isBulk={false}
          isTeacherEvaluationEnabled={isTeacherEvaluationEnabled}
          isStudentSubmissionEnabled={isStudentSubmissionEnabled}
          isPrivate={isPrivate}
          studentName={studentIdentifierName}
          isSubmitted={isSubmitted}
          isSubmissionLocked={
            _.get(currentStudentData, "lockingState.state") === "LOCKED"
          }
          isDraftSubmission={isDraftSubmission}
          allowMovingTaskToReviewed={isOnlyStudentLeftToEvaluate}
          handleCancelClick={onCloseDialogBox}
          handleConfirmClick={onDialogConfirmClick}
          assignmentTitle={itemTitle}
          isLTIAttachmentPresent={isLTIAttachmentPresent}
          totalLinkedAssignments={_.size(assignmentLinkedClasses)}
          notEvaluatedCount={isOnlyStudentLeftToEvaluate ? 1 : null}
          isIncludedInCourseFlow={_.get(
            assignmentData,
            "content.isIncludedInCourseFlow",
            false
          )}
          pendingAIEvaluation={
            pendingAIEvaluation &&
            isAiEvaluationEnabled &&
            _.includes(
              ["EVALUATE_QUICK_APPROVE", "EVALUATE_SHARE_QUICK_APPROVE"],
              currentDialogAction
            )
          }
          isWorksheetOrAiTutor={isWorksheet || isAITutor}
        />
      ) : null}

      {isWorksheetRoutes ? (
        <HeaderItem
          t={t}
          leftItem={getLeftItem()}
          text={headerLabel}
          subText={
            <AllSubmissionsSubText
              isWorksheetLayoutUpdateEnabled={isWorksheetLayoutUpdateEnabled}
              {...props}
            />
          }
          rightItem={allSubmissionRightItem}
          onInstructionsClick={onInstructionsClick}
          leftItemStyle={styles.allSubmissionLeftItemStyle}
          rightPane={rightPane}
          isScheduled={isScheduled}
          isDraft={isDraft}
          onClickEdit={onClickEdit}
          onClickAssign={onClickAssign}
          rightExtraItems={rightExtraItems}
          isSafeBrowserEnabled={isSafeBrowserEnabled}
          isWorksheet={isWorksheet}
          setToastMsg={setToastMsg}
          safeExamBrowserExitCode={safeExamBrowserExitCode}
          isWorksheetLayoutUpdateEnabled={isWorksheetLayoutUpdateEnabled}
          isArchivedClass={isArchivedClass}
          isGradingPeriodUnlocked={isGradingPeriodUnlocked}
          isCurrentAcademicYearSelected={isCurrentAcademicYearSelected}
          isClassStreamManagerRoute={isClassStreamManagerRoute}
          isClassRoomAssignmentsRoute={isClassRoomAssignmentsRoute}
          isGradebookRoute={isGradebookRoute}
          isCoursePlanRoute={isCoursePlanRoute}
          isCalendarRoute={isCalendarRoute}
          showBreadCrumbs={showBreadCrumbs}
          breadcrumbHierarchy={showBreadCrumbs ? breadcrumbHierarchy : null}
          onBreadCrumbClick={handleBreadCrumbClick}
          isLinksRoute={isLinksRoute}
          isAllSubmissionsNavigationImprovementsEnabled={
            isAllSubmissionsNavigationImprovementsEnabled
          }
        />
      ) : null}

      {(isWorkbookEnabled || oldPdftronEnabled) && (
        <div className={classes.disableOverlay} />
      )}

      {!_.isEmpty(draftAction) ? (
        <CreateOrEditAssignment
          mode="edit"
          assignment={assignmentData}
          course={
            !_.isEmpty(courseData)
              ? courseData
              : _.get(assignmentData, "course", {})
          }
          courseId={courseId}
          creationSource="CLASSROOM"
          creationSubSource="CLASSROOM_DETAILS"
          onClose={onCloseEditAssignmentModal}
          isSettingsStepOnly={draftAction === "ASSIGN"}
          modalContent={transparentModalClass}
          overlayContainer={transparentModalClass}
        />
      ) : null}
      {!_.isEmpty(sendAlertMessageModalParams) ? (
        <SendAlertMessageModal
          setSendAlertMessageModalParams={setSendAlertMessageModalParams}
          selectedStudentsSubmissions={[]}
          sendAlertMessageModalParams={sendAlertMessageModalParams}
          sortedAssignedStudents={sortedAssignedStudents}
          assignmentDeadline={_.get(assignmentData, "deadline", {})}
          assignmentId={_.get(assignmentData, "id", null)}
          isStudentSubmissionEnabled={isStudentSubmissionEnabled}
          isBackdateAssignment={_.get(
            assignmentData,
            "isBackdateAssignment",
            false
          )}
        />
      ) : null}
    </div>
  );
});

HeaderV2.defaultProps = {
  showEvaluationDropdown: true,
  isLinksRoute: false,
  showAssignmentActionsDropdown: true,
  showSendAlertMesssageModalIcon: true,
};

const mapActionCreators = {
  unHandStudentAssignmentSubmission,
  setToastMsg,
  onClickEvalUpdate,
  goToRelativeRoute,
  navigateToNextStudent,
  navigateToPreviousStudent,
  getAssessmentObj,
  goToBack,
  handleQuickApproveAIEvaluation,
  updateStateForAssignment,
  setAIGradingProgress,
  createAiAssessmentEvaluation,
  getToddleDocAttachment,
};

const mapStateToProps = (state, ownProps) => {
  const { studentAssignmentId, allFields } = ownProps;
  const attachmentId = _.get(ownProps, "location.query.currentMedia", null);
  const isArchivedClass = _.get(
    state,
    "teacher.selected_class.isArchivedClass"
  );

  const isCurrentAcademicYearSelected = _.get(
    state,
    "platform.academicYearSelected.isCurrentAcademicYear",
    true
  );

  const userInfo = _.get(state, "login.userInfo", {});

  const isGraphTheaterModeEnabled = _.get(
    state,
    "worksheet.isGraphTheaterModeEnabled"
  );

  const { redirectionSource } = _.get(state, "navigation.navigationObject", {});

  const isAIGeneratingResponseForTool = getIsAIGeneratingResponseForTool({
    state,
    studentAssignmentId,
    checkForAllTools: true,
    assessmentFields: allFields,
  });

  return {
    isArchivedClass,
    isCurrentAcademicYearSelected,
    userInfo,
    pdfTronInstance: _.get(state, "classRoom.pdfTronInstance", null),
    currentCurriculumProgramId: getSelectedCurriculumId(state),
    isGraphTheaterModeEnabled,
    redirectionSource,
    isAIGeneratingResponseForTool,
    attachmentId,
  };
};

const getDerivedProps = ownProps => {
  const { userInfo, assessmentTools } = ownProps;

  const userType = _.get(userInfo, "user_type");

  const someToolsHaveEval = getSomeToolsHaveEval({
    assessmentTools,
  });

  return { userType, someToolsHaveEval };
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  withDerivedProps(getDerivedProps),
  I18nHOC({ resource: ["common", "assessmentEvaluation"] }),
  withQueryParamsBasedInterface({ cb: getClassroomPropsMemoize })
)(HeaderV2);
